(ns app.front.view.group.admin.inspections.checklists.checklists
  "Group admin config section for inspection checklists, including their items and item groups,
  defined in `app.front.view.group.admin.inspections.checklist-items`"
  (:require [app.domain.inspection.model :as inspection-model]
            [app.front.entity-edit :as ee]
            [app.front.ui :as ui]
            [app.front.ui.column-select :as columns]
            [app.front.ui.i18n :as ui-i18n]
            [app.front.ui.select :as ui-select]
            [app.front.ui.separator :as ui-sep]
            [app.front.ui.tab-panel :as tab-panel]
            [app.front.view.group.admin.inspections.checklist-result-sets :as inspection-checklist-result-sets]
            [app.front.view.group.admin.inspections.checklists.checklist-items :as inspection-checklist-items]
            [app.front.view.group.admin.inspections.checklists.subs :as checklist-subs]
            [app.front.view.group.admin.subs :as gas]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [re-frame.core-instrumented :as rf]))

(defn checklist-name [checklist-template language]
  (i18n-model/get-text (:inspection.checklist/names checklist-template)
                       language))

(defn checklist-selector
  "List of all inspection checklist templates under the currently existing
  inspection template kinds (= inspection types) and a button to add new ones"
  [{:keys [tr language]}]
  (let [kind-id          (<sub [::checklist-subs/selected-template-kind-id])
        template-id      (<sub [::checklist-subs/selected-checklist-template-id])
        format-date-time (<sub [::date-util/date-time-fn])]
    [:div.grid.grid-cols-6.gap-4 {:data-cy "checklist-selector"}
     [:div.col-span-2
      [columns/column-select
       {:tr        tr
        :title     :inspection/available-template-kinds
        :light?    true
        :options   (<sub [::checklist-subs/inspection-template-kind-items])
        :on-change #(>evt [::checklist-subs/select-template-kind %])}]]
     [:div.col-span-4
      [columns/column-select
       {:tr        tr
        :title     :inspection/checklists-for-type
        :light?    true
        :options   (map (fn [{id :db/id :as checklist-template}]
                          {:id        id
                           :selected? (= id template-id)
                           :content   [:div.grid.grid-cols-12.gap-2
                                       [:div.col-span-4
                                        (checklist-name checklist-template language)]
                                       [:div.col-span-6
                                        {:class (when (not= id template-id) "text-gray-600")}
                                        (when-let [change (:latest-change checklist-template)]
                                          (str (format-date-time (:ts change)) " (" (:user change) ")"))]
                                       [:div.col-span-2.uppercase.bold.flex.justify-end
                                        (when (:inspection.template.kind/_default-checklist-templates checklist-template)
                                          (tr [:inspection.checklist/default]))]]})
                        (<sub [::checklist-subs/inspection-checklist-templates]))
        :on-change #(>evt [::checklist-subs/select-checklist-template %])
        :actions   (when kind-id
                     [{:tr-key   :inspection.checklist/add-new
                       :icon     i/plus
                       :on-click #(>evt [::checklist-subs/create-checklist-template kind-id language])}])}]]]))

(defn checklist-editor-buttons
  [{:keys [tr]}]
  (let [reordering? (<sub [::checklist-subs/reordering?])]
    [:div.flex.flex-row.gap-3
     [ui/modal-confirmation-button
      {:tr                  tr
       :id                  "delete-checklist-template"
       :disabled?           reordering?
       :on-yes-click        #(>evt [::checklist-subs/delete-checklist-template])
       :trigger-button-text (tr [:inspection.checklist/delete])
       :trigger-button-opts {:kind :ghost
                             :size :small
                             :icon i/trash}
       :modal-title         (tr [:are-you-sure])}]
     (cc/button {:kind     :ghost
                 :size     :small
                 :icon     i/arrows-up-down
                 :on-click #(>evt [::checklist-subs/toggle-reordering])}
                (if reordering?
                  (tr [:inspection.checklist/reorder-done])
                  (tr [:inspection.checklist/reorder])))
     (cc/button {:kind     :tertiary
                 :size     :small
                 :disabled reordering?
                 :on-click #(>evt [::checklist-subs/set-default-checklist-template
                                   (not (<sub [::checklist-subs/default-checklist-template?]))])}
                (if (<sub [::checklist-subs/default-checklist-template?])
                  (tr [:inspection.checklist/set-default-false])
                  (tr [:inspection.checklist/set-default-true])))]))

(defn result-set-name [tr lang result-set]
  (if (keyword? result-set)
    (tr [result-set])
    (->> result-set
         ((apply juxt inspection-model/checklist-result-set-entity-name-attributes))
         (map #(i18n-model/get-text-from-entities-or-map % lang))
         (str/join "/"))))

(defn checklist-editor-fields [{:keys [tr language]}]
  (let [checklist                    (<sub [::checklist-subs/selected-checklist-template])
        checklist-id                 (:db/id checklist)
        unsaved                      (<sub [::ee/unsaved-for checklist-id])
        on-edit                      #(rf/dispatch-sync [::ee/set-value checklist-id %1 %2])
        on-save                      #(>evt [::ee/save nil :inspection/edit-checklist-template checklist-id %1 %2])
        selected-result-set          (or (:inspection.checklist/custom-result-set checklist)
                                         (:inspection.checklist/result-set checklist))
        selected-result-set-removed? (-> selected-result-set
                                         :domain/state
                                         (= :inspection.checklist.result-set.state/removed))]
    [:form.grid.grid-cols-2.gap-4.bg-gray-200.px-2.py-4.rounded-sm
     {:on-submit #(.preventDefault %)}
     [ui-i18n/localized-string-edit
      {:id                  "inspection-checklist-template-name"
       :class               "colspan-1"
       :label-text          (tr [:inspection.checklist/names])
       :on-change           #(on-edit :inspection.checklist/names %)
       :on-end-edit         #(on-save :inspection.checklist/names %)
       :strings             (or (:inspection.checklist/names unsaved)
                                (-> checklist :inspection.checklist/names i18n-model/strings-map))
       :allowed-locales-sub ::gas/allowed-locales
       :auto-focus          false}]
     [:div
      [ui-select/select
       {:id           "inspection-checklist-template-result-set"
        :class        "colspan-1 w-full"
        :light        true
        :label-text   (tr [:inspection.checklist/result-set])
        :allow-empty? false
        :value        selected-result-set
        :val->str     (partial result-set-name tr language)
        :values       (concat inspection-model/checklist-result-sets
                              (<sub [::inspection-checklist-result-sets/checklist-result-sets])
                              (when selected-result-set-removed?
                                [selected-result-set]))
        :on-change    #(apply on-save
                              (if (keyword? %)
                                [:inspection.checklist/result-set %]
                                [:inspection.checklist/custom-result-set (:db/id %)]))}]
      (when selected-result-set-removed?
        [:div.text-ui-red (tr [:inspection.checklist/selected-result-set-removed])])]]))

(defn checklist-editor
  "Editor for a single inspection checklist template, with its item templates and item groups"
  [{:keys [language] :as ctx}]
  [:<>
   [ui-sep/horizontal-line]
   [:div.flex.flex-col.mt-8
    [:div.mb-1.flex.justify-between.items-baseline
     [:div.bold.uppercase
      (checklist-name (<sub [::checklist-subs/selected-checklist-template]) language)]
     [checklist-editor-buttons ctx]]
    [:div.mt-6
     [checklist-editor-fields ctx]
     [inspection-checklist-items/editor ctx]]]])

(defn view-tabbed
  "Inspection checklist template editor for the group admin.
  At the top is the list of all checklist templates for the group.x
  The rest of the view is the editor for the selected template."
  [{:keys [tr] :as ctx}]
  (let [language (<sub [::ui-i18n/editor-language ::gas/allowed-locales])
        ctx      (assoc ctx :language language)]
    [tab-panel/tab-panel
     {:id        "inspection-config-tabs-checklists"
      :class     "mt-8 h-full"
      :tab-class "h-full bg-ui-violet-20"
      :key->str  #(tr [%])
      :tabs      [{:key     :inspection.checklist/configure-checklists
                   :content [:div.mt-6
                             [checklist-selector ctx]
                             (when (<sub [::checklist-subs/selected-checklist-template-id])
                               [checklist-editor ctx])]}
                  {:key     :inspection.checklist/configure-result-sets
                   :content [:div.mt-6
                             [inspection-checklist-result-sets/editor ctx]]}]}]))

(defn view [ctx]
  [view-tabbed ctx])
